import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageLayout from "../../_layout/PageLayout";
import { DeleteInspection, DeleteInspectionFromAssignedProject, ManageDeletedData, ViewInpsectionsFromAssignedProject, ViewInspectionsList } from "../../../services/authorization/Permissions";
import { CurrentProjectStorageModel } from "../../../services/project/CurrentProjectStorage";
import useUrlParams from "../../../services/url/UrlParamsService";
import { api } from "../../../services/api/ApiService";
import { ProjectMenuItemsModel } from "../ProjectMenuItems";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { useHistory } from "react-router-dom";
import DataGrid from "../../../components/datatable/DataGrid";
import { ProjectInspectionsStorageModel } from "../../../services/inspection/ProjectInspectionsStorage";
import { ActionsSection, ButtonsSection } from "../../../components/data-components/sections/DataSections";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import useErrorHandler, { ApplicationError } from "../../../services/error-handler/ErrorHandlerService";
import { BadRequestError } from "../../../services/api/BadRequestError";
import { MarkRelevantSmall } from "../../../components/svg/svgImageList";
import { CompareArrows } from "@mui/icons-material";
import useThemService from "../../../services/theme-service/ThemeService";
import Button from "../../../components/button/Button";
import CompareButton from "../../../components/buttons/compare-button/CompareButton";
import { notificationsService } from "../../../services/notifications/NotificationsService";
import { modalNotificationsService } from "../../../services/notifications/ModalNotificationService";

export default observer(function ProjectInspectionsPage() {
    const history = useHistory()
    const { themeClass } = useThemService();
    const urlParamsService = useUrlParams();
    const { handleErrorInSnackbar, handleErrorInModal } = useErrorHandler();
    const projectId = useMemo(() => urlParamsService.getProjectId()!, [urlParamsService]);
    const [currentProjectStorage] = useState(() =>
        CurrentProjectStorageModel.create({ projectId: projectId }, { api })
    );

    const [projectInspectionsStorage] = useState(() =>
        ProjectInspectionsStorageModel.create(
          {
            projectId: projectId,
            useGridFiltering: true,
            defaultColumnVisibility: {
              AIScore: false
            }
          },
          { api }
        )
    );

    const projectMenuItems = useMemo(
        () =>
          ProjectMenuItemsModel.create({
            projectId: projectId,
            isLoading: currentProjectStorage.isLoading,
            numberOfSewerObjects: currentProjectStorage.currentProject.sewerObjectsCount,
          }),
        [
          projectId,
          currentProjectStorage.isLoading,
          currentProjectStorage.currentProject.sewerObjectsCount,
        ]
    );

    const menuItems = {
        staticItems: projectMenuItems.getStaticItems(),
    };

    const deleteInspections = useCallback(async() => {
      projectInspectionsStorage.setIsDeletingInspections(true);
      try {
        projectInspectionsStorage.deleteInspections(projectInspectionsStorage.selectedItems);
      } catch (error) {
        const applicationError = error as ApplicationError;

        if (applicationError as BadRequestError) {
          applicationError.withModalTitleKey("general.sewer-objects.delete-error.title");
          handleErrorInModal(applicationError);
        } else {
          applicationError.withErrorMessage("default-error.sewer-objects.delete-from-project");
          handleErrorInSnackbar(applicationError);
        }
      }
      projectInspectionsStorage.setIsDeletingInspections(false);
    }, [])

    const isDeleteDisabled = useMemo(() => {
      return !projectInspectionsStorage.selectedItemsCount
        || !permissionsService.userHasOneOfThePermissions([
          DeleteInspection,
          DeleteInspectionFromAssignedProject,
        ]);
    }, [projectInspectionsStorage.selectedItemsCount])

    useEffect(() => {
        breadcrumbsStorage.setEntityNames([
          {
            entity: EntityTypeFromPath.Project,
            name: currentProjectStorage.currentProject.name,
          },
        ]);
    
        breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }, [currentProjectStorage.currentProject.name, history.location.pathname]);

    const setRelevanceFilter = useCallback(() => {
      projectInspectionsStorage.setGridFilter(
        {...projectInspectionsStorage.filterData.gridFilter!, 
          items: [{field: "IsRelevant", operator: "is", value: "true"}]}
        , true);
    }, [])

    const onCompare = useCallback(async () => {
      await projectInspectionsStorage.updateComparison();
      await currentProjectStorage.load();
    }, [])

    const onCompareFinished = useCallback(async (notification: Notification) => {
      if (!notification.data.success) {
        modalNotificationsService.showError({titleKey: "general.inspection.compare.failed", messageKey: "general.inspection.compare.failed.description"});
      }
      await projectInspectionsStorage.load();
      await currentProjectStorage.load();
    }, [])

    return (
        <PageLayout
            accessWithOneOfThePermissions={[ViewInspectionsList, ViewInpsectionsFromAssignedProject]}
            titleKey="ProjectInspections.title"
            titleContext={{
                projectName:
                    currentProjectStorage.currentProject && currentProjectStorage.currentProject.name,
            }}
            menuItems={menuItems}
        >
          <ActionsSection>
            <ButtonsSection>
              <CompareButton 
                onClick={onCompare} 
                onCompareFinished={onCompareFinished} 
                lastCompared={currentProjectStorage.currentProject.lastCompared}
                disabled={currentProjectStorage.currentProject.comparisonInProgress}
              />
              <DeleteButton 
                disabled={isDeleteDisabled}
                confirmationAction={() => deleteInspections()} 
                confirmationModalSubtitleKey="ProjectInspections.delete.confirmation"
                confirmationModalTitleContext={{inspectionsCount: projectInspectionsStorage.selectedItems.length}}
                confirmationModalTitleKey="ProjectInspections.delete.confirmation.title"
                canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
              />
            </ButtonsSection>
          </ActionsSection>
            <DataGrid
                itemTypeKey="general.sewer-objects"
                dataGridStorage={projectInspectionsStorage}
                withTopSpacing
                customSelectAll
                toolbarCustomButton={{icon: MarkRelevantSmall, textKey: "general.inspection.relevance.set-relevant", callback: () => setRelevanceFilter()}}
            />
        </PageLayout>
    )
});